import type { Workflow } from "../../zodios/apiDefinitions/workflows";
import type { IngestionModel, IngestionPhase, PhaseStatus, RecentFailure, SubStream } from "./types";

type ProgressPair = readonly [number, number] | null | undefined;

export interface OnboardingProgressInput {
  recentWorkflow: Workflow | undefined;
  backfillWorkflow: Workflow | undefined;
  recentPercent: number | null;
  backfillPercent: number | null;
  recentPhase: string | null;
  enrichmentSqpr: ProgressPair;
  enrichmentSqprSettled: boolean;
  /** no ASINs were ever requested, so no week will land — render N/A, not 0% */
  enrichmentSqprNothingRequested: boolean;
  sellerAnalyticsStatus: PhaseStatus | "none";
  sellerAnalyticsPercent: number | null;
  recentUsable: boolean | null;
  shouldShow: boolean;
  // Failures are surfaced to staff only for now
  isStaff: boolean;
  isVendor: boolean;
}

const statusOf = (workflow: Workflow | undefined): PhaseStatus | "none" => {
  if (!workflow) return "none";
  if (workflow.status === "SUCCESS") return "done";
  if (workflow.status === "FAILED" || workflow.status === "CANCELLED") return "failed";
  return "running";
};

const clampPercent = (value: number | null | undefined): number => {
  if (value == null || Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
};

const getEnrichmentPhase = (input: {
  sellerAnalyticsStatus: PhaseStatus | "none";
  sellerAnalyticsPercent: number | null;
  enrichmentSqpr: ProgressPair;
  enrichmentSqprSettled: boolean;
  enrichmentSqprNothingRequested: boolean;
  visibleStatus: (status: PhaseStatus) => PhaseStatus;
}): IngestionPhase => {
  const {
    sellerAnalyticsStatus,
    sellerAnalyticsPercent,
    enrichmentSqpr,
    enrichmentSqprSettled,
    enrichmentSqprNothingRequested,
    visibleStatus,
  } = input;
  // A real seller-analytics failure softened to `running` for non-staff, dev will still get alerted via Slack and the failure will be visible to all staff users.
  const delayed = sellerAnalyticsStatus === "failed" && visibleStatus("failed") !== "failed";

  const pageViews: SubStream =
    sellerAnalyticsStatus === "none" ? { key: "page_views", status: "queued", percent: null }
    : sellerAnalyticsStatus === "done" ? { key: "page_views", status: "done", percent: 100 }
    : {
        key: "page_views",
        status: visibleStatus(sellerAnalyticsStatus),
        percent: clampPercent(sellerAnalyticsPercent),
      };

  const searchQuery: SubStream = ((): SubStream => {
    if (!enrichmentSqpr) return { key: "search_query", status: "queued", percent: null };
    const [completed, total] = enrichmentSqpr;
    if (total <= 0 || completed >= total) return { key: "search_query", status: "done", percent: 100 };
    if (enrichmentSqprNothingRequested) return { key: "search_query", status: "not_applicable", percent: null };
    const percent = clampPercent((completed / total) * 100);
    if (enrichmentSqprSettled) return { key: "search_query", status: "done", percent };
    return { key: "search_query", status: "running", percent };
  })();

  const subStreams: SubStream[] = [pageViews, searchQuery];

  // A not-applicable stream can't hold the phase back, and can't advance it either.
  const statuses = subStreams.map((sub) => sub.status).filter((s): s is PhaseStatus => s !== "not_applicable");
  const status: PhaseStatus =
    statuses.length === 0 ? "done"
    : statuses.every((s) => s === "queued") ? "queued"
    : statuses.some((s) => s === "failed") ? "failed"
    : statuses.some((s) => s === "running") ? "running"
    : "done";

  return { key: "enrichment", status, percent: null, subStreams, delayed };
};

const RECENT_ONLY_TERMINAL_PHASE = "complete";

export const buildIngestionModel = (input: OnboardingProgressInput): IngestionModel => {
  const visibleStatus = (status: PhaseStatus): PhaseStatus =>
    !input.isStaff && status === "failed" ? "running" : status;

  const recentRaw = statusOf(input.recentWorkflow);
  const usable = input.recentUsable === true;

  const blockingFailure = recentRaw === "failed" && !usable;
  const recentFailure: RecentFailure =
    !blockingFailure ? "none"
    : input.isStaff ? "blocking"
    : "delayed";

  const recentStatus: PhaseStatus =
    recentRaw === "none" ? "queued"
    : recentRaw === "failed" ?
      usable ? "done"
      : input.isStaff ? "failed"
      : "running"
    : recentRaw;

  const isReady = usable || recentRaw === "done";
  const recent: IngestionPhase = {
    key: "recent",
    status: recentStatus,
    percent: isReady ? 100 : clampPercent(input.recentPercent),
    delayed: recentFailure === "delayed",
  };

  if (input.recentPhase === RECENT_ONLY_TERMINAL_PHASE) {
    return { phases: [recent], isReady, isComplete: isReady, recentFailure, shouldShow: input.shouldShow };
  }

  const backfillRaw = statusOf(input.backfillWorkflow);
  const backfillDelayed = backfillRaw === "failed" && visibleStatus("failed") !== "failed";
  const backfillStatus: PhaseStatus = visibleStatus(backfillRaw === "none" ? "queued" : backfillRaw);
  const backfillDone = backfillStatus === "done";
  const backfill: IngestionPhase = {
    key: "backfill",
    status: backfillStatus,
    percent: backfillDone ? 100 : clampPercent(input.backfillPercent),
    delayed: backfillDelayed,
  };

  const enrichment =
    input.isVendor ? null : (
      getEnrichmentPhase({
        sellerAnalyticsStatus: input.sellerAnalyticsStatus,
        sellerAnalyticsPercent: input.sellerAnalyticsPercent,
        enrichmentSqpr: input.enrichmentSqpr,
        enrichmentSqprSettled: input.enrichmentSqprSettled,
        enrichmentSqprNothingRequested: input.enrichmentSqprNothingRequested,
        visibleStatus,
      })
    );

  return {
    phases: enrichment ? [recent, backfill, enrichment] : [recent, backfill],
    isReady,
    isComplete: isReady && backfillDone,
    recentFailure,
    shouldShow: input.shouldShow,
  };
};
